import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Lock, ShoppingBag, Users } from "lucide-react";
import { Footer } from "@/components/landing/Footer";
import { PageHeader } from "@/components/landing/PageHeader";
import { useCart } from "@/components/landing/cart/CartContext";
import { CartLine } from "@/components/landing/cart/CartLine";
import { CouponBox, DestinationToggle, ShippingOptions, useShippingRates } from "@/components/landing/cart/CheckoutParts";
import { PayPalButton } from "@/components/landing/cart/PayPalButton";
import { PreOrderNotice } from "@/components/landing/cart/PreOrderNotice";
import { DonationBox } from "@/components/landing/cart/DonationBox";
import { getRef, getRefInfo } from "@/components/ambassadors/RefCapture";
import { SuggestedProducts } from "@/components/landing/SuggestedProducts";
import { money } from "@/components/landing/useProducts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEST_KEY = "dd_destination";

export default function Cart() {
  const { items, count, subtotal } = useCart();
  const navigate = useNavigate();
  const [destination, setDestination] = useState(() => localStorage.getItem(DEST_KEY) || "us");
  const rates = useShippingRates(destination);
  const [rateId, setRateId] = useState(null);
  const [coupon, setCoupon] = useState(null);
  const [donation, setDonation] = useState(0);
  const ref = getRefInfo();
  const [busy, setBusy] = useState(false);

  useEffect(() => { localStorage.setItem(DEST_KEY, destination); }, [destination]);
  useEffect(() => { if (rates?.length && !rates.find((r) => r.id === rateId)) setRateId(rates[0].id); }, [rates, rateId]);

  const rate = rates?.find((r) => r.id === rateId);
  const shipping = rate ? (rate.free_over_cents != null && subtotal >= rate.free_over_cents ? 0 : rate.amount_cents) : 0;
  const discount = coupon ? (coupon.kind === "percent" ? Math.min(subtotal, Math.round((subtotal * coupon.value) / 100)) : Math.min(subtotal, Math.round(coupon.value * 100))) : 0;
  const total = Math.max(0, subtotal - discount) + donation + shipping;
  const payload = () => ({
    items: items.map((i) => ({ lookup_key: i.lookupKey, size: i.size, color: i.color, quantity: i.quantity })),
    shipping_rate_id: rate.id, destination, coupon_code: coupon?.code || null, donation_cents: donation, ref_code: getRef(),
  });

  const checkout = async () => {
    if (!rate) return toast.error("Choose a delivery option.");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/payments/checkout-cart`, { ...payload(), origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout. Try again.");
      setBusy(false);
    }
  };

  return (
    <main data-testid="cart-page" className="bg-[#ffffff] text-[#0a0a0b] min-h-screen antialiased">
      <PageHeader page="cart" link={{ id: "shop", to: "/shop", label: "Shop" }} />
      <section className="relative pt-28 sm:pt-32 pb-24 px-5 sm:px-8">
        <div className="max-w-[1200px] mx-auto">
          <p className="font-mono-d text-[11px] uppercase tracking-[0.3em] text-[#ff2a85] mb-4 flex items-center gap-3"><span className="diamond" /> Checkout</p>
          <h1 className="font-display font-extrabold uppercase leading-[0.9] text-4xl sm:text-5xl tracking-tight">Your <span className="text-pink">bag</span> <span data-testid="cart-count" className="font-mono-d text-sm tracking-[0.2em] text-[#5f5e5a] align-middle ml-3">{count} item{count === 1 ? "" : "s"}</span></h1>

          {items.length === 0 ? (
            <div data-testid="cart-empty" className="mt-16 max-w-md">
              <ShoppingBag size={32} className="text-[#c9c8c2]" />
              <p className="mt-5 font-display font-extrabold uppercase text-lg">Your bag is empty</p>
              <p className="mt-2 text-[#5f5e5a]">Pieces from Drop 001 are limited — grab yours before they're gone.</p>
              <Link to="/shop" data-testid="cart-empty-shop" className="mt-8 inline-flex h-12 px-7 items-center rounded-full bg-[#0a0a0b] text-white font-mono-d text-[10px] uppercase tracking-[0.2em] hover:bg-[#ff2a85] transition-colors">Shop the drop</Link>
              <SuggestedProducts sources={[]} title="Start with these" limit={4} testId="cart-empty-suggestions" />
            </div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} className="mt-10 grid grid-cols-[minmax(0,1fr)] lg:grid-cols-12 gap-10 lg:gap-14">
              <div className="min-w-0 lg:col-span-7">
                <ul data-testid="cart-lines" className="border-t border-[#e3e2dc]">{items.map((i) => <CartLine key={`${i.lookupKey}|${i.size}|${i.color}`} item={i} />)}</ul>
                <SuggestedProducts sources={items.map((i) => ({ id: i.productId, category: i.category, related: [] }))} title="Complete the look" limit={4} testId="cart-suggestions" />
              </div>

              <aside className="min-w-0 lg:col-span-5">
                <div className="lg:sticky lg:top-24 rounded-3xl border border-[#e3e2dc] bg-[#faf9f6] p-6 sm:p-7 space-y-7">
                  <div>
                    <p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#5f5e5a] mb-3">Ship to</p>
                    <DestinationToggle value={destination} onChange={setDestination} />
                  </div>
                  <div>
                    <p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#5f5e5a] mb-3">Delivery</p>
                    <ShippingOptions rates={rates} subtotal={subtotal} value={rateId} onChange={setRateId} />
                    <PreOrderNotice compact />
                  </div>
                  <div>
                    <p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#5f5e5a] mb-3">Coupon</p>
                    <CouponBox subtotal={subtotal} coupon={coupon} onApply={setCoupon} onClear={() => setCoupon(null)} />
                  </div>
                  <DonationBox value={donation} onChange={setDonation} />
                  {ref && (
                    <p data-testid="cart-referral-badge" className="flex items-center gap-2 rounded-2xl border border-[#e3e2dc] bg-[#faf9f6] px-4 py-3 font-mono-d text-[10px] uppercase tracking-[0.15em] text-[#5f5e5a]">
                      <Users size={13} className="text-[#ff2a85] shrink-0" /> Shopping via {ref.name ? `${ref.name}'s` : "an Ambassador"} link · code {ref.code} · credited on every piece
                    </p>
                  )}
                  <dl className="space-y-2.5 text-sm border-t border-[#e3e2dc] pt-5">
                    <Row label="Subtotal" value={money(subtotal)} testId="summary-subtotal" />
                    {discount > 0 && <Row label={`Discount · ${coupon.code}`} value={`-${money(discount)}`} testId="summary-discount" accent />}
                    {donation > 0 && <Row label="Donation · Palestine" value={money(donation)} testId="summary-donation" accent />}
                    <Row label={rate ? `Delivery · ${rate.label}` : "Delivery"} value={rate ? (shipping === 0 ? "Free" : money(shipping)) : "—"} testId="summary-shipping" />
                    <div className="flex items-center justify-between pt-3 border-t border-[#e3e2dc]">
                      <dt className="font-display font-extrabold uppercase text-sm">Total</dt>
                      <dd data-testid="summary-total" className="font-display font-extrabold text-xl">{money(total)} <span className="font-mono-d text-[10px] text-[#5f5e5a]">USD</span></dd>
                    </div>
                  </dl>
                  <button data-testid="cart-checkout" onClick={checkout} disabled={busy || !rate} className="w-full h-14 rounded-full bg-[#ff2a85] hover:bg-[#e60067] text-white font-mono-d text-[11px] uppercase tracking-[0.2em] disabled:opacity-60 flex items-center justify-center gap-2 transition-colors">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <><Lock size={13} /> Checkout — {money(total)}</>}
                  </button>
                  <PayPalButton
                    testId="cart-paypal-button"
                    disabled={!rate}
                    build={() => (rate ? payload() : null)}
                  />
                  <p className="text-[11px] text-[#5f5e5a] text-center leading-relaxed">Secure checkout by Stripe or PayPal · Apple Pay &amp; Google Pay on card checkout. Taxes (if any) are calculated on the next step. Pre-order · ships when Drop 001 is made.</p>
                  <button type="button" data-testid="cart-continue" onClick={() => navigate("/shop")} className="w-full font-mono-d text-[10px] uppercase tracking-[0.2em] text-[#5f5e5a] hover:text-[#0a0a0b]">Continue shopping</button>
                </div>
              </aside>
            </motion.div>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
}

const Row = ({ label, value, testId, accent }) => (
  <div className="flex items-center justify-between gap-4">
    <dt className="font-mono-d text-[10px] uppercase tracking-[0.2em] text-[#5f5e5a]">{label}</dt>
    <dd data-testid={testId} className={`font-mono-d ${accent ? "text-[#007a3d]" : ""}`}>{value}</dd>
  </div>
);
